import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useCoafCommunicationDetail,
  useApproveCoafCommunication,
  useRejectCoafCommunication,
  useSendCoafCommunication,
  useExportCoafCommunication,
  getStatusLabel,
  getStatusColor,
  getTypeLabel,
  getOperationTypeLabel,
  getSuspicionIndicatorLabel,
} from '../../../hooks/useCoaf';
import { ArrowLeft, FileText, User, AlertTriangle, CheckCircle, XCircle, Send, Download, Clock } from 'lucide-react';

export default function CoafCommunicationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: communication, isLoading, error } = useCoafCommunicationDetail(id!);
  
  const approveMutation = useApproveCoafCommunication();
  const rejectMutation = useRejectCoafCommunication();
  const sendMutation = useSendCoafCommunication();
  const exportMutation = useExportCoafCommunication();
  
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleApprove = async () => {
    if (!confirm('Confirma a aprovação desta comunicação?')) return;
    try {
      await approveMutation.mutateAsync(id!);
    } catch (err) {
      console.error('Erro ao aprovar:', err);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    try {
      await rejectMutation.mutateAsync({ id: id!, reason: rejectReason });
      setShowRejectModal(false);
    } catch (err) {
      console.error('Erro ao rejeitar:', err);
    }
  };

  const handleSend = async () => {
    if (!confirm('Confirma o envio desta comunicação ao COAF?')) return;
    try {
      await sendMutation.mutateAsync(id!);
    } catch (err) {
      console.error('Erro ao enviar:', err);
    }
  };

  const handleExport = async (format: 'xml' | 'pdf') => {
    try {
      const blob = await exportMutation.mutateAsync({ id: id!, format });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coaf-${id?.slice(0, 8)}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao exportar:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-700 rounded w-1/4"></div>
          <div className="h-64 bg-surface-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !communication) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
          Erro ao carregar comunicação
        </div>
      </div>
    );
  }

  const canApprove = communication.status === 'PENDING_APPROVAL';
  const canSend = communication.status === 'APPROVED';
  const canExport = ['SENT', 'CONFIRMED'].includes(communication.status);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/compliance/coaf/list')}
            className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-7 h-7 text-primary-400" />
              Comunicação COAF
            </h1>
            <p className="text-surface-400">#{communication.id.slice(0, 8)}</p>
          </div>
        </div>
        
        <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(communication.status)}`}>
          {getStatusLabel(communication.status)}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Operation Info */}
          <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
            <h2 className="text-lg font-semibold mb-4">Informações da Operação</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-surface-400">Tipo de Comunicação</label>
                <p className="font-medium">{getTypeLabel(communication.type)}</p>
              </div>
              <div>
                <label className="text-sm text-surface-400">Tipo de Operação</label>
                <p className="font-medium">{getOperationTypeLabel(communication.operationType)}</p>
              </div>
              <div>
                <label className="text-sm text-surface-400">Data da Operação</label>
                <p className="font-medium">
                  {new Date(communication.operationDate).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <label className="text-sm text-surface-400">Valor</label>
                <p className="font-medium text-lg">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(communication.operationValue)}
                </p>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="text-sm text-surface-400">Descrição</label>
              <p className="text-surface-300 mt-1">{communication.operationDescription}</p>
            </div>
          </div>

          {/* Involved Parties */}
          <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              Partes Envolvidas
            </h2>
            
            <div className="space-y-4">
              {communication.involvedParties?.map((party: any, index: number) => (
                <div key={index} className="p-4 bg-surface-700/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{party.name}</span>
                    <span className="text-sm text-surface-400">{party.role}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-surface-400">Documento:</span>
                      <span className="ml-2 font-mono">{party.document}</span>
                    </div>
                    <div>
                      <span className="text-surface-400">Tipo:</span>
                      <span className="ml-2">{party.documentType}</span>
                    </div>
                    {party.email && (
                      <div>
                        <span className="text-surface-400">E-mail:</span>
                        <span className="ml-2">{party.email}</span>
                      </div>
                    )}
                    {party.phone && (
                      <div>
                        <span className="text-surface-400">Telefone:</span>
                        <span className="ml-2">{party.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {(!communication.involvedParties || communication.involvedParties.length === 0) && (
                <p className="text-surface-400 text-center py-4">Nenhuma parte envolvida registrada</p>
              )}
            </div>
          </div>

          {/* Suspicion Indicators */}
          <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Indicadores de Suspeição
            </h2>
            
            <div className="flex flex-wrap gap-2">
              {communication.suspicionIndicators?.map((indicator: string, index: number) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-yellow-500/10 text-yellow-400 rounded-full text-sm"
                >
                  {getSuspicionIndicatorLabel(indicator)}
                </span>
              ))}
              {(!communication.suspicionIndicators || communication.suspicionIndicators.length === 0) && (
                <p className="text-surface-400">Nenhum indicador registrado</p>
              )}
            </div>
            
            {communication.additionalInfo && (
              <div className="mt-4">
                <label className="text-sm text-surface-400">Informações Adicionais</label>
                <p className="text-surface-300 mt-1">{communication.additionalInfo}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
            <h3 className="font-semibold mb-4">Ações</h3>
            <div className="space-y-3">
              {canApprove && (
                <>
                  <button
                    onClick={handleApprove}
                    disabled={approveMutation.isPending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    <CheckCircle className="w-5 h-5" />
                    {approveMutation.isPending ? 'Aprovando...' : 'Aprovar'}
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                    Rejeitar
                  </button>
                </>
              )}
              
              {canSend && (
                <button
                  onClick={handleSend}
                  disabled={sendMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                  {sendMutation.isPending ? 'Enviando...' : 'Enviar ao COAF'}
                </button>
              )}
              
              {canExport && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleExport('xml')}
                    disabled={exportMutation.isPending}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-surface-700 hover:bg-surface-600 rounded-lg font-medium transition-colors"
                  >
                    <Download className="w-5 h-5" />
                    XML
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    disabled={exportMutation.isPending}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-surface-700 hover:bg-surface-600 rounded-lg font-medium transition-colors"
                  >
                    <Download className="w-5 h-5" />
                    PDF
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
            <h3 className="font-semibold mb-4">Histórico</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-500"></div>
                <div>
                  <p className="text-sm font-medium">Criada</p>
                  <p className="text-xs text-surface-400">
                    {new Date(communication.createdAt).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
              
              {communication.approvedAt && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-green-500"></div>
                  <div>
                    <p className="text-sm font-medium">Aprovada</p>
                    <p className="text-xs text-surface-400">
                      {new Date(communication.approvedAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
              )}
              
              {communication.rejectedAt && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-red-500"></div>
                  <div>
                    <p className="text-sm font-medium">Rejeitada</p>
                    <p className="text-xs text-surface-400">
                      {new Date(communication.rejectedAt).toLocaleString('pt-BR')}
                    </p>
                    {communication.rejectionReason && (
                      <p className="text-sm text-surface-300 mt-1">{communication.rejectionReason}</p>
                    )}
                  </div>
                </div>
              )}
              
              {communication.submittedAt && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-primary-500"></div>
                  <div>
                    <p className="text-sm font-medium">Enviada</p>
                    <p className="text-xs text-surface-400">
                      {new Date(communication.submittedAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Protocol */}
          {communication.protocolNumber && (
            <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
              <h3 className="font-semibold mb-2">Protocolo COAF</h3>
              <p className="font-mono text-lg text-primary-400">{communication.protocolNumber}</p>
            </div>
          )}

          {/* Related Alert */}
          {communication.pldAlertId && (
            <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
              <h3 className="font-semibold mb-2">Alerta PLD Relacionado</h3>
              <a
                href={`/admin/compliance/pld/alerts/${communication.pldAlertId}`}
                className="text-primary-400 hover:text-primary-300 font-mono text-sm"
              >
                #{communication.pldAlertId.slice(0, 8)}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface-800 rounded-xl p-6 w-full max-w-md border border-surface-700">
            <h3 className="text-lg font-semibold mb-4">Rejeitar Comunicação</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Motivo da Rejeição</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Descreva o motivo da rejeição..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || rejectMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-medium disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Rejeitando...' : 'Confirmar Rejeição'}
              </button>
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
